import * as React from 'react';
import { UserState } from '../../../interface/MyInterfaces';
import { VendorReportState2025, VendorReportAction2025, UserVendorReportState } from '../../../interface/VendorReportInterface2025';
import { withUserActionTracker } from '../../../sharedUtilities/UserActionTracker';
import { ReportText, TextLink, TextSpan } from "../../Common/SharedStyledComponents";
import * as Helpers from '../../Common/Helpers';

interface extraProp {
    hasCensusData: boolean
}

class MethodologyTab extends React.Component<UserState & VendorReportState2025 & extraProp & VendorReportAction2025 & UserVendorReportState, {}> {
    constructor(props) {
        super(props);
    }

    public render() {

        const displayId = this.props.display_id;
        const isRu4m = displayId == "999079" || displayId == "999086" || displayId == "999082";
        const isCpupc = displayId == "999080" || displayId == "999085" || displayId == "999081";

        let about_the_data = "";
        if (isRu4m) {
            about_the_data = "This report includes data from RU4M universities that are members of IRIS and that have submitted the relevant data for FY2021-FY2025: Michigan State University, Michigan Technological University, University of Michigan, and Wayne State University.";
        } else if (isCpupc) {
            about_the_data = "This report includes data from CPUPC universities that are members of IRIS and that have submitted the relevant data for FY2021-FY2025: The University of Texas at Arlington, Texas State University, University of Texas at San Antonio, University of Texas at Dallas, Texas A&M University, and University of North Texas.";
        }

        const isAggregateDisplay = Helpers.isAgg(displayId);
        const selectedYear = this.props.VendorReport_Tab12025?.selectedYear;
        const minYear = this.props.VendorReport_Tab12025?.minYear;
        const maxYear = this.props.VendorReport_Tab12025?.maxYear;
        const yearRange = minYear && maxYear ? `${minYear}-${maxYear}` : selectedYear;
        const hasHardcodedIntroList = isRu4m || isCpupc;
        const benchmarkUniversityList = this.props.VendorReport_Tab12025?.benchmarkUniversityList || "";
        const aggUniversityList = this.props.VendorReport_Tab12025?.allMemberUniversities || "";
        const allIrisAvailabilityNote = Helpers.isAllIris(this.props.institutionName)
            ? ". Due to variations in data availability, some years may not include data from all IRIS members"
            : "";
        const appendedAggSentence =
            isAggregateDisplay && !hasHardcodedIntroList && this.props.VendorReport_Tab12025 && yearRange
                ? `This includes data from IRIS member universities that have reported the relevant data for FY ${yearRange}${allIrisAvailabilityNote}${aggUniversityList ? `: ${aggUniversityList}` : ""}.`
                : "";

        return <div className='tabcontents container-fluid vendor-report-content'>
            <div className='row'>
                <div className='col-md-12'>
                    <h3>Vendor Profile Report Methodology</h3>
                    <p>Details on how the reports are created, what they show, and the process of
                        matching different datasets</p>

                    <h6 className="levelTwoHeader">Introduction</h6>
                    <p>
                        The IRIS Vendor Profile Report demonstrates the economic impact of universities’ research
                        spending by describing the industries, geographic areas, and types of businesses that receive
                        contracts and subawards for goods and services through research grants. The report shows the
                        dollars of research funding spent; the number and types of establishments receiving funding,
                        including national, in-state, and small businesses; and whether these figures change from
                        year to year. Data include both federal and non-federal research expenditures. {about_the_data}
                    </p>
                    {appendedAggSentence && <p>{appendedAggSentence}</p>}

                    <h6 className="levelTwoHeader">How to understand the results</h6>

                    <h6 className="levelTwoHeader">The Vendor Profile tab</h6>
                    <p>
                        This section provides an overview of the economic impacts of research spending by showing direct
                        expenditures to internal university units and outside vendors and subcontractors. This section
                        shows such spending at the national level, as well as with vendors classified as in-state and
                        small businesses.
                    </p>
                    <p>
                        Yearly spending, including both vendor spending and subawards, is included on the line graphs
                        for the last five fiscal years for which your university has sent complete data. Research spending
                        and establishments are displayed separately for national, in-state, and small businesses. The bar
                        chart shows how the distribution of your university's research expenditures with in-state and
                        small businesses compares with all other IRIS universities in the selected year.
                    </p>
                    {
                        !Helpers.isAllIris(this.props.institutionName) && (!isAggregateDisplay || hasHardcodedIntroList) && this.props.VendorReport_Tab12025 && selectedYear &&
                        <p>
                            The benchmarking chart includes data from IRIS member universities that have reported the relevant data for FY {yearRange}, excluding {this.props.institutionName}. Due to variations in data availability, some years may not include data from all IRIS members.{benchmarkUniversityList ? ` For FY ${selectedYear}, the benchmarking chart includes data from: ${benchmarkUniversityList}.` : ""}
                        </p>
                    }
                    <h6 className="levelTwoHeader">The Industry Revenue Analysis tab</h6>
                    <p>
                        The sunburst display shows industry spending as represented by the vendors’ NAICS codes, over the
                        most recent five fiscal years of complete university data (see below for a description of NAICS codes). In
                        this visualization, NAICS codes are assigned to organizations based on the NAICS of the
                        establishment(s) with the largest revenue. You can view spending by NAICS industry sector,
                        sub-sector, or group. Some additional spending is not displayed in this tab because it could
                        not be matched with NAICS codes. Please see the Data Matching section for more details.
                    </p>
                    <h6 className="levelTwoHeader">How the reports are created</h6>
                    <p>
                        Building the vendor report is a multi-step process that begins with the vendor and subawardee
                        name and address information submitted by universities.
                    </p>
                    <h6 className="levelTwoHeader">Data Cleaning</h6>
                    <p>
                        IRIS removes names of individual people, standardizes business names and addresses to correct
                        common data entry errors, and generates a unique list of vendor and subawardee records. Quality
                        checks ensure valid disambiguated names, reasonable transaction dates, and that vendors are
                        located in the United States. All spending metrics are expressed in real {this.props.BVDMatchRates.inflation_year} dollars.
                    </p>
                    <h6 className="levelTwoHeader">Entity Resolution</h6>
                    <p>
                        Vendors and subawardees are grouped based on name similarity and geographic proximity. A single
                        canonical vendor or subawardee name is assigned to all the vendors within each group using string
                        similarity and payment volume.  A single canonical location is also assigned within each group.
                        This ensures consistent identification and counts of unique vendors and subawardees despite minor
                        name variations.
                    </p>
                    <h6 className="levelTwoHeader">Data Matching</h6>
                    <p>To classify vendors by industry and business size, IRIS matches standardized vendor records to
                        internal business directory data. IRIS first uses geographic information and company name similarity
                        to identify likely matches between vendor records and business records. Geographic proximity is
                        evaluated using H3 spatial indexing, while company names and addresses are compared using string
                        similarity methods. Potential matches that are less certain are reviewed using a large language
                        model (LLM) to help reduce false positive matches. Records that remain unmatched then undergo a
                        second round of matching focused primarily on company names to identify additional valid matches.
                        These second-pass matches are also reviewed by the LLM before being included in the final results.
                        This multi-step approach is designed to maximize match coverage while maintaining a high level of
                        accuracy.
                    </p>
                    <h6 className="levelTwoHeader">Summary of Vendor Coverage and Match Results</h6>
                    <p>
                        {this.props.VendorReport_MatchRateTab && this.props.VendorReport_MatchRateTab.bvd_match}
                    </p>
                    <h6 className="levelTwoHeader">NAICS Codes</h6>
                    <p>The North American Industry Classification System (NAICS) is a system for classifying establishments
                        (individual business locations). For this report, NAICS codes are used to classify businesses
                        according to the types of economic production in which they participate. The codes are organized
                        into broad industry sectors and more specific groupings. The first two digits of a NAICS code
                        represent general categories of economic activities. Nested within the two-digit codes, a third
                        digit represents the subsector, and within that, a fourth digit represents the industry group.
                        IRIS matches payments to vendors and subawardees at the four-digit NAICS level. For this report,
                        we use the 2022 NAICS codes. A small number of industry records contained legacy NAICS codes,
                        so equivalent 2017 NAICS codes were used when necessary to ensure complete coverage of spending
                        across industry subsectors. The codes are revised every five years. The retail and publishing
                        sectors saw some of the most significant changes in the latest revision. A summary of the
                        differences between the 2022 and 2017 codes can be found on the <a href="https://www.bls.gov/sae/additional-resources/details-on-the-conversion-to-the-2022-north-american-industry-classification-system-naics-from-2017-naics.htm">
                        Bureau of Labor Statistics website.</a>
                    </p>

                    <h6 className="levelTwoHeader">Limitations</h6>
                    <p>Only vendors and subawardees with U.S. addresses are included in this report. This is done to
                        ensure a consistent basis across all metrics, since some metrics, such as NAICS codes, cannot
                        be applied to spending in other countries.</p>
                    <p>A vendor address in administrative records may be an out-of-state billing address rather than
                        the address of a local establishment that provided a service. To the extent that this occurs,
                        local spending will be underestimated because the spending will be attributed to the
                        out-of-state billing address.</p>
                    <p>We continue to work to improve the quality of matching and data production in order to address
                        these limitations.</p>
                    <p>Payments to individual people were not included in this report. The data underlying this report
                        represent research funding from federal and non-federal sources. You may download the underlying
                        data by clicking the “Download Data” link at the top and bottom of the report. The report can
                        be shared by clicking the “Share Report” link at the top of the report.
                    </p>

                    <h6 className="levelTwoHeader">Citation: </h6>
                    <p>Vendor Profile Report, Institute for Research on Innovation and Science
                        (IRIS), {this.props.vendorRelease}</p>


                    <ReportText center>
                        <TextSpan bold>Website: </TextSpan><TextLink href="https://iris.isr.umich.edu/"
                            target="_blank">iris.isr.umich.edu</TextLink> |
                        <TextSpan bold> Email: </TextSpan><TextLink href="mailto:IRIS-info@umich.edu"
                            target="_blank">IRIS-info@umich.edu</TextLink> |
                        <TextSpan bold> LinkedIn: </TextSpan><TextLink href="https://www.linkedin.com/company/iris-um"
                            target="_blank">linkedin.com/company/iris-um</TextLink>
                    </ReportText>
                </div>
            </div>
        </div>
    }
}

export default withUserActionTracker(MethodologyTab, "Vendor Profile Report", "viewed Methodology Tab");
